import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DialogTitle, useConfirmService } from '@onesource/components';
import { TProDirectory, TProDirectoryRevision, TUpdateProDirectory } from '@onesource/schemas';
import {
  useDeleteMyProDirectoryRevisionMutation,
  useGetMyProDirectoryRevisionsQuery,
  useUpdateProDirectoryMutation,
} from '@onesource/services';
import { FC, useState } from 'react';
import { MdDelete, MdExpandLess, MdExpandMore, MdHistory, MdRefresh, MdSave } from 'react-icons/md';

import { RevisionDetail } from './revision-detail';
import { RevisionSidebarList } from './revisions-sidebar';
import { ProDirectoryRevisionsDialogSkeleton } from './revisions-skeleton';
import { approvedToDataShape } from './revisions-utils';

interface Props {
  open: boolean;
  onClose: () => void;
  approvedData: TProDirectory | null;
}

export const ProDirectoryRevisionsDialog: FC<Props> = ({ open, onClose, approvedData }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: revisions = [], isLoading, refetch } = useGetMyProDirectoryRevisionsQuery(open);
  const deleteMutation = useDeleteMyProDirectoryRevisionMutation();
  const resubmitMutation = useUpdateProDirectoryMutation();
  const { setConfirm } = useConfirmService();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isMutating = deleteMutation.isPending || resubmitMutation.isPending;

  const displayRevisions = revisions
    .filter(r => r.status !== 'approved')
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const selectedRevision = displayRevisions.find(r => r.id === selectedId) ?? displayRevisions[0] ?? null;
  const approved = approvedToDataShape(approvedData);

  const handleClose = (_: unknown, reason?: string) => {
    if (isMutating) {
      return;
    }
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  const handleDelete = (revision: TProDirectoryRevision) => {
    setConfirm({
      open: true,
      title: 'Delete Revision?',
      message: 'This pending revision will be permanently deleted.',
      confirmBtnLabel: 'Delete',
      onConfirm: () => {
        deleteMutation.mutate({ revisionId: revision.id });
        if (selectedId === revision.id) {
          setSelectedId(null);
        }
      },
    });
  };

  // Map a stored revision's snake_case data back to the update payload. Includes
  // every field so a resubmit / save-as-draft is a faithful copy of the revision.
  const revisionToPayload = (revision: TProDirectoryRevision, isDraft: boolean): TUpdateProDirectory => {
    const d = revision.data;
    return {
      isDraft,
      slug: d.slug ?? undefined,
      bio: d.bio ?? undefined,
      profession: d.profession ?? undefined,
      otherProfession: d.other_profession ?? undefined,
      companyName: d.company_name ?? undefined,
      companyEmail: d.company_email ?? undefined,
      professionalWebsite: d.professional_website ?? undefined,
      offerings: d.offerings ?? undefined,
      teachingFormat: d.teaching_format ?? undefined,
      expertise: d.expertise ?? undefined,
      languagesTaught: d.languages_taught ?? undefined,
      casualImage: d.casual_image ?? undefined,
      landscapeImage: d.landscape_image ?? undefined,
      fullName: d.full_name ?? undefined,
      title: d.title ?? undefined,
      phone: d.phone ?? undefined,
      businessStreet: d.business_street ?? undefined,
      businessCountryId: d.business_country_id ?? undefined,
      businessStateId: d.business_state_id ?? undefined,
      businessCityId: d.business_city_id ?? undefined,
      certificates: d.certificates ?? undefined,
      socials: d.socials ?? undefined,
    };
  };

  const handleResubmit = (revision: TProDirectoryRevision) => {
    setConfirm({
      open: true,
      title: 'Resubmit Revision?',
      message: 'This will resubmit this revision for admin review. Any current pending revision will be replaced.',
      confirmBtnLabel: 'Resubmit',
      onConfirm: () => {
        resubmitMutation.mutate(revisionToPayload(revision, false), {
          onSuccess: () => {
            setSelectedId(null);
            refetch();
          },
        });
      },
    });
  };

  const handleSaveAsDraft = (revision: TProDirectoryRevision) => {
    setConfirm({
      open: true,
      title: 'Save as Draft?',
      message: "This will save this revision's data as your current draft. Any existing draft will be replaced.",
      confirmBtnLabel: 'Save as Draft',
      onConfirm: () => {
        resubmitMutation.mutate(revisionToPayload(revision, true), {
          onSuccess: () => {
            setSelectedId(null);
            refetch();
          },
        });
      },
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth fullScreen={fullScreen} disableEscapeKeyDown>
      <DialogTitle onClose={isMutating ? undefined : onClose} icon={<MdHistory />}>
        Pro Directory Revisions
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: fullScreen ? 'column' : 'row', minHeight: 400 }}>
        {isLoading ? (
          <ProDirectoryRevisionsDialogSkeleton />
        ) : (
          <>
            {displayRevisions.length > 0 && (
              <>
                {fullScreen ? (
                  <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box
                      onClick={() => {
                        if (!isMutating) {
                          setSidebarOpen(v => !v);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        cursor: isMutating ? 'not-allowed' : 'pointer',
                        bgcolor: 'action.hover',
                        opacity: isMutating ? 0.6 : 1,
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        REVISIONS ({displayRevisions.length})
                      </Typography>
                      <IconButton size="small" disabled={isMutating}>
                        {sidebarOpen ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
                      </IconButton>
                    </Box>
                    <Collapse in={sidebarOpen}>
                      <Box sx={{ p: 1.5 }}>
                        <RevisionSidebarList
                          revisions={displayRevisions}
                          selectedRevision={selectedRevision}
                          isMutating={isMutating}
                          onSelect={id => setSelectedId(id)}
                          onResubmit={handleResubmit}
                          onSaveAsDraft={handleSaveAsDraft}
                          onDelete={handleDelete}
                          onSelectCallback={() => setSidebarOpen(false)}
                        />
                      </Box>
                    </Collapse>
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        width: { sm: 180, md: 220 },
                        flexShrink: 0,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        overflowY: 'auto',
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="text.secondary"
                        sx={{ mb: 1, display: 'block' }}
                      >
                        REVISIONS
                      </Typography>
                      <RevisionSidebarList
                        revisions={displayRevisions}
                        selectedRevision={selectedRevision}
                        isMutating={isMutating}
                        onSelect={id => setSelectedId(id)}
                        onResubmit={handleResubmit}
                        onSaveAsDraft={handleSaveAsDraft}
                        onDelete={handleDelete}
                      />
                    </Box>
                    <Divider orientation="vertical" flexItem />
                  </>
                )}
              </>
            )}

            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pt: 2, pb: 3 }}>
              {!selectedRevision ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    minHeight: 200,
                  }}
                >
                  <Typography color="text.secondary">No revisions found.</Typography>
                </Box>
              ) : (
                <RevisionDetail revision={selectedRevision} approved={approved} />
              )}
            </Box>
          </>
        )}
      </DialogContent>

      {selectedRevision?.status === 'pending' && (
        <>
          <Divider />
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<MdDelete size={18} />}
              disabled={isMutating}
              onClick={() => handleDelete(selectedRevision)}
            >
              Delete Revision
            </Button>
          </DialogActions>
        </>
      )}

      {selectedRevision?.status === 'rejected' && (
        <>
          <Divider />
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Typography variant="caption" color="error.main" sx={{ mr: '20px' }}>
              This revision was rejected. You can resubmit it for review or save it as a draft to edit.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<MdSave size={18} />}
              disabled={isMutating}
              onClick={() => handleSaveAsDraft(selectedRevision)}
            >
              Save as Draft
            </Button>
            <Button
              variant="contained"
              startIcon={<MdRefresh size={18} />}
              disabled={isMutating}
              onClick={() => handleResubmit(selectedRevision)}
            >
              Resubmit for Review
            </Button>
            {/* Per-row Save-as-Draft also exists in the sidebar for every non-draft revision. */}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
